export interface StepPacingTimerConfig {
  enabled: boolean;
  duration_seconds: number;
  label: string;
}

export interface PacingTimerRuntimeState {
  step_id: number;
  status: 'ready' | 'running' | 'ended';
  started_at?: string | null;
  duration_seconds: number;
  label?: string;
}

const DEFAULT_DURATION_SECONDS = 300;
const MAX_DURATION_SECONDS = 60 * 60 * 3;

const clampDurationSeconds = (value: unknown): number => {
  const parsed = Number(value);
  if (!Number.isFinite(parsed)) return 0;
  const rounded = Math.floor(parsed);
  if (rounded <= 0) return 0;
  return Math.min(rounded, MAX_DURATION_SECONDS);
};

export const normalizeStepPacingTimer = (content: any): StepPacingTimerConfig => {
  const source = content && typeof content === 'object' && !Array.isArray(content)
    ? (content.pacing_timer && typeof content.pacing_timer === 'object' && !Array.isArray(content.pacing_timer)
      ? content.pacing_timer
      : {})
    : {};

  let durationSeconds = clampDurationSeconds(
    source.duration_seconds
    ?? source.durationSeconds,
  );
  if (!durationSeconds) {
    const minutes = clampDurationSeconds(source.duration_minutes ?? source.minutes);
    durationSeconds = minutes ? minutes * 60 : 0;
  }

  const enabled = source.enabled === true || (source.enabled == null && durationSeconds > 0);
  return {
    enabled,
    duration_seconds: enabled ? (durationSeconds || DEFAULT_DURATION_SECONDS) : durationSeconds,
    label: typeof source.label === 'string' ? source.label.trim() : '',
  };
};

const parseStartedAtMs = (value?: string | null): number | null => {
  if (!value) return null;
  const parsed = Date.parse(value);
  return Number.isFinite(parsed) ? parsed : null;
};

export const formatTimerClock = (totalSeconds: number): string => {
  const safeSeconds = Math.max(0, Math.floor(totalSeconds));
  const hours = Math.floor(safeSeconds / 3600);
  const minutes = Math.floor((safeSeconds % 3600) / 60);
  const seconds = safeSeconds % 60;
  if (hours > 0) {
    return `${hours}:${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
  }
  return `${String(minutes).padStart(2, '0')}:${String(seconds).padStart(2, '0')}`;
};

export const getStepPacingTimerState = (
  content: any,
  startedAt?: string | null,
  nowMs = Date.now(),
) => {
  const config = normalizeStepPacingTimer(content);
  if (!config.enabled || config.duration_seconds <= 0) return null;
  const startedAtMs = parseStartedAtMs(startedAt);
  if (startedAtMs == null) return null;
  const elapsedSeconds = Math.max(0, Math.floor((nowMs - startedAtMs) / 1000));
  const remainingSeconds = Math.max(0, config.duration_seconds - elapsedSeconds);
  const warningSeconds = Math.min(60, Math.max(15, Math.floor(config.duration_seconds * 0.2)));
  return {
    ...config,
    elapsed_seconds: elapsedSeconds,
    remaining_seconds: remainingSeconds,
    expired: remainingSeconds <= 0,
    warning: remainingSeconds > 0 && remainingSeconds <= warningSeconds,
    started_at_ms: startedAtMs,
  };
};
